import React, { FC, useRef, useEffect } from 'react';
import classnames from 'classnames';

import { RSStepHeaderItem } from './components/RSStepHeaderItem';

import styles from './index.module.css';

export interface RSStepHeaderProps {
  items: {
    itemKey: string;
    title: string;
    isError?: boolean;
  }[];
  activeItemKey?: string;
  onItemClick: (itemKey: string) => void;
  // activeItemKeyの変更時にviewportから見切れる場合に自動でスクロールするかどうか
  isAutoScroll?: boolean;
}

export const RSStepHeader: FC<RSStepHeaderProps> = ({
  items,
  activeItemKey,
  onItemClick,
  isAutoScroll,
}) => {
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isAutoScroll) return;

    const scrollAreaElement = scrollAreaRef.current;

    if (!scrollAreaElement) return;

    const itemWrapperElements = scrollAreaElement.querySelectorAll(
      '[data-step-header-item-wrapper]',
    );

    if (itemWrapperElements.length === 0) return;

    const activeItemWrapperElement = Array.from(itemWrapperElements).find(
      (ele) =>
        ele.getAttribute('data-step-header-item-wrapper') === activeItemKey,
    ) as HTMLElement;

    if (!activeItemWrapperElement) return;

    const scrollAreaElementRect = scrollAreaElement.getBoundingClientRect();
    const activeItemWrapperElementRect =
      activeItemWrapperElement.getBoundingClientRect();

    if (
      // アイテムの左端が見切れている場合
      scrollAreaElementRect.left > activeItemWrapperElementRect.left
    ) {
      // active要素のviewportからの左の位置 - スクロール要素のviewportからの左の位置 = スクロール要素から見たactive要素の現在の左の位置
      // スクロール要素から見たactive要素の現在の左の位置 + スクロール要素のスクロール量 - 12 = スクロール要素の先頭からのactive要素の左の位置
      // スクロール要素の先頭からのactive要素の左の位置 - padding(12px) = スクロール量
      const left =
        activeItemWrapperElementRect.left -
        scrollAreaElementRect.left +
        scrollAreaElement.scrollLeft -
        12;
      scrollAreaElement.scrollTo({
        left,
        behavior: 'smooth',
      });
    } else if (
      // アイテムの右端が見切れている場合
      scrollAreaElementRect.right < activeItemWrapperElementRect.right
    ) {
      // active要素のviewportからの左の位置 - スクロール要素のviewportからの左の位置 = スクロール要素から見たactive要素の現在の左の位置
      // スクロール要素から見たactive要素の現在の左の位置 + スクロール要素のスクロール量 - 12 = スクロール要素の先頭からのactive要素の左の位置
      // スクロール要素の先頭からのactive要素の左の位置 - スクロール要素の幅 + active要素の幅 + padding(12px) = スクロール量
      const left =
        activeItemWrapperElementRect.left -
        scrollAreaElementRect.left +
        scrollAreaElement.scrollLeft -
        scrollAreaElementRect.width +
        activeItemWrapperElementRect.width +
        12;
      scrollAreaElement.scrollTo({
        left,
        behavior: 'smooth',
      });
    }
  }, [activeItemKey, isAutoScroll]);

  return (
    <div className={classnames('w-full', 'bg-gray-10', styles['root'])}>
      <div
        ref={scrollAreaRef}
        className="relative mx-auto box-border max-w-[536px] overflow-x-auto overflow-y-visible"
      >
        <div className="pc:justify-between pc:space-x-0 box-border flex space-x-1 px-1.5 py-1">
          {items.map((item) => {
            // 内部がflex要素だと親要素のpaddingが効かないため、擬似要素を入れている
            return (
              <div
                key={item.itemKey}
                className="relative flex-none last-of-type:after:absolute last-of-type:after:left-full last-of-type:after:top-0 last-of-type:after:block last-of-type:after:h-full last-of-type:after:w-[12px] last-of-type:after:content-['']"
                data-step-header-item-wrapper={item.itemKey}
              >
                <RSStepHeaderItem
                  itemKey={item.itemKey}
                  title={item.title}
                  isActive={item.itemKey === activeItemKey}
                  isError={item.isError}
                  onClick={onItemClick}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
